import { useEffect, useRef, useState } from "react";

import formatDuration from "@/utils/formaty";

export default function useAudio(src) {
  const audioRef = useRef(null);

  const [status, setStatus] = useState("paused");
  const [restorePlaying, setRestorePlaying] = useState(false);
  const [volume, setVolume] = useState(100);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playable, setPlayable] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    const audio = new Audio(src);

    audioRef.current = audio;
    audio.load();

    const handleTimeUpdate = () => setProgress(audio.currentTime);
    const handleLoaded = () => {
      setStatus("paused");
      setPlayable(true);
      setProgress(0);
      setDuration(audio.duration);
    };
    const handlePlay = () => setStatus("playing");
    const handlePause = () => setStatus("paused");
    const handleEnded = () => setStatus("stopped");
    const handleVolume = () => {
      setVolume(Math.round(audio.volume * 100));
      setIsMuted(audio.muted);
    };
    const handleError = () => {
      setPlayable(false);
      setDuration(0);
      setStatus("error");
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoaded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("volumechange", handleVolume);
    audio.addEventListener("error", handleError);

    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoaded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("volumechange", handleVolume);
      audio.removeEventListener("error", handleError);
    };
  }, [src]);

  // Position & Play
  const seek = (newTime) => {
    const audio = audioRef.current;
    if (!audio) return;

    if (status === "playing") {
      setRestorePlaying(true);
    }

    audio.pause();
    audio.currentTime = newTime;
    setProgress(newTime);
  };

  const seekFinish = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (restorePlaying) {
      audio.play();
      setRestorePlaying(false);
    }
  };

  const playPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (status === "playing") {
      audio.pause();
    } else {
      audio.play();
    }
  };

  // Volume
  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.muted = !audio.muted;

    setIsMuted(audio.muted);

    if (volume < 1) {
      changeVolume(1);
    }
  };

  const toggleVolume = () => {
    if (volume <= 32) changeVolume(33);
    else if (volume <= 65) changeVolume(66);
    else if (volume <= 99) changeVolume(100);
    else if (volume === 100) changeVolume(0);
  };

  const changeVolume = (volume) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.volume = volume / 100;
    setVolume(volume);
    setIsMuted(audio.muted);
  };

  // Display
  const secondsToTime = (time) => {
    if (!time) return "0:00";
    return formatDuration(time);
  };

  return {
    playable,
    changeVolume,
    duration,
    isMuted,
    playPause,
    progress,
    secondsToTime,
    seek,
    seekFinish,
    status,
    toggleMute,
    toggleVolume,
    volume,
  };
}
